import { useEffect } from 'react';
import { ApolloError } from '@apollo/client';

import { useErrorHandler, useProductCategory, useProducts, useSlugProductMode } from '~hooks';
import { Product, ProductCategory } from '~types/appsync.types';

interface UseProductCategoryResolverOptions {
  productCategoryShortname?: string;
  /** If true, errors will be propagated to the ErrorBoundary. Default: true */
  propagateErrors?: boolean;
}

interface UseProductCategoryResolverResult {
  productCategory: ProductCategory | undefined;
  products: Product[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  /** Error specific to product category (domain/duration) fetching */
  productCategoryError: ApolloError | undefined;
  /** Error specific to products fetching */
  productsError: ApolloError | undefined;
  isSlugProductMode: boolean;
}

/**
 * Unified hook that resolves product category and products data.
 * Handles both standard mode (by shortname) and slug mode (by product slug).
 * Automatically propagates errors to the ErrorBoundary unless propagateErrors is set to false.
 */
const useProductCategoryResolver = (
  options: UseProductCategoryResolverOptions = {},
): UseProductCategoryResolverResult => {
  const { productCategoryShortname, propagateErrors = true } = options;
  const { slugProduct, isSlugProductMode } = useSlugProductMode();
  const throwError = useErrorHandler();

  const [products, loadingProducts, productsError] = useProducts({
    productCategoryShortname,
    productSlug: slugProduct,
  });

  // Explicitly check that products array has at least one item before accessing
  const hasSlugProduct = isSlugProductMode && !!products?.[0];
  const productFromSlug = hasSlugProduct ? products[0] : undefined;

  // Use a single useProductCategory call with conditional parameters
  const [productCategory, loadingCategory, productCategoryError] = useProductCategory({
    productCategoryId: hasSlugProduct ? productFromSlug?.productCategoryId : undefined,
    productCategoryShortname: isSlugProductMode ? undefined : productCategoryShortname,
    validityCategoryId: hasSlugProduct ? productFromSlug?.validityCategory?.id : undefined,
  });

  const loading = loadingProducts || loadingCategory;

  // Propagate errors to ErrorBoundary (centralized error handling)
  useEffect(() => {
    if (propagateErrors && productCategoryError && !loading) {
      throwError(productCategoryError);
    }
  }, [propagateErrors, productCategoryError, loading, throwError]);

  useEffect(() => {
    if (propagateErrors && productsError && !loading) {
      throwError(productsError);
    }
  }, [propagateErrors, productsError, loading, throwError]);

  return {
    error: productsError ?? productCategoryError,
    isSlugProductMode,
    loading,
    productCategory,
    productCategoryError,
    products,
    productsError,
  };
};

export default useProductCategoryResolver;
